// P9.2A §16 — Generation-lineage view model for the Strategy Builder.
//
// Two sources:
//   • lineageViewFromRecord   — a live GeneratedStrategyRecord (complete §8 metadata)
//   • lineageViewFromSavedBlocks — a saved/loaded block graph. Structural provenance is
//     detected from the markers the P9.2 compiler embeds (strategy_config.setupSlug +
//     setup_state machine). ANYTHING ELSE displays exactly:
//         "generated by legacy_block_first_v1"
//     — historical lineage is never migrated, relabeled, or embellished.
//
// Every displayed field is derived from artifacts actually present; absent facts render as
// honest absences ("none recorded"), never fabricated values.
//
// Purity: no I/O, no clock, no randomness.

import { BlockType, type Block } from '../../types';
import type { MachineDefinition } from '../setup/setupStateMachine';
import type { GeneratedStrategyRecord } from './structuralSetupProfile';

export interface BlockWhy { blockId: string; name: string; stage: string; why: string }

export interface GenerationLineageView {
  profileId: 'structural_setup_v1' | 'legacy_block_first_v1';
  profileVersion: number | null;
  /** The single line the Builder must show for legacy saves. */
  profileLabel: string;
  proposition: string | null;           // ResearchIdeaBank proposition (or config description)
  ribPropositionId: string | null;
  setupIrId: string | null;
  setupSlug: string | null;
  stages: string[] | null;
  temporalOrdering: string[] | null;    // human lines: 'stage: WITHIN n bars of prior'
  stateTransitions: string[] | null;    // 'state → next (on feed)'
  timeframes: string[] | null;
  dataDependencies: string[] | null;
  referencesAndRegions: string[] | null;
  confirmations: string[] | null;
  invalidation: string[] | null;
  expiry: string | null;
  targetThesis: string[] | null;
  rawBlockCount: number;
  semanticBlockCount: number | null;
  evidenceState: string;
  commercialTargetState: string;
  unsupportedCapabilities: string[];
  /** Why each block exists — a complex graph is never shown unexplained. */
  blockExplanations: BlockWhy[];
}

type D = Record<string, unknown>;
const d = (b: Block): D => (b.data ?? {}) as unknown as D;
const s = (v: unknown): string | null => (typeof v === 'string' && v.length ? v : null);

export function lineageViewFromRecord(rec: GeneratedStrategyRecord): GenerationLineageView {
  const h = rec.hypothesis;
  return {
    profileId: rec.profileId,
    profileVersion: rec.profileVersion,
    profileLabel: `${rec.profileId} v${rec.profileVersion}`,
    proposition: h.proposition,
    ribPropositionId: rec.ribPropositionId,
    setupIrId: rec.setupIrId,
    setupSlug: rec.setupSlug,
    stages: h.sequence.map((x) => x.stage),
    temporalOrdering: h.sequence.filter((x) => x.temporal).map((x) => `${x.stage} (${x.requires}): ${x.temporal!.op} ${x.temporal!.bars} bars`),
    stateTransitions: rec.compiled.machine.states.map((st, i, arr) => st.advanceOn === null ? `${st.name} (terminal)` : `${st.name} → ${st.advanceTo ?? arr[i + 1]?.name} (on ${st.advanceOn})`),
    timeframes: [`HTF ${h.timeframes.htf}`, ...(h.timeframes.setup ? [`setup ${h.timeframes.setup}`] : []), `trigger ${h.timeframes.trigger}`],
    dataDependencies: [rec.requiredData, `status: ${rec.requiredDataStatus}`, ...h.entities.map((e) => `${e.instrument}.${e.field}@${e.timeframe}`)],
    referencesAndRegions: h.regions.map((r) => `${r.id}: ${r.kind}${r.window ? ` (${r.window} bars)` : ''}`),
    confirmations: h.sequence.filter((x) => x.stage === 'confirmation').map((x) => `${x.requires} — ${x.why}`),
    invalidation: h.invalidation.map((i) => `${i.kind}: ${i.description}`),
    expiry: (() => { const e = h.sequence.find((x) => x.stage === 'expiry'); return e ? `expires after ${e.params?.expiresAfterBars ?? '—'} bars, cooldown ${e.params?.cooldownBars ?? 0}` : null; })(),
    targetThesis: h.exitThesis.filter((e) => e.kind !== 'fixed_r_control').map((e) => `${e.kind}: ${e.description}`),
    rawBlockCount: rec.rawBlockCount,
    semanticBlockCount: rec.semanticBlockCount,
    evidenceState: rec.evidenceState,
    commercialTargetState: rec.commercialTargetState,
    unsupportedCapabilities: rec.unsupportedCapabilities,
    blockExplanations: rec.compiled.complexity.explanations.map((e) => ({ blockId: e.blockId, name: e.role, stage: e.stage, why: e.why })),
  };
}

export function lineageViewFromSavedBlocks(blocks: Block[]): GenerationLineageView {
  const cfg = blocks.find((b) => b.type === BlockType.STRATEGY_CONFIG);
  const machineBlock = blocks.find((b) => b.type === BlockType.SETUP_STATE);
  const setupSlug = cfg ? s(d(cfg).setupSlug) : null;
  const structural = !!setupSlug && !!machineBlock;

  if (!structural) {
    // Historical lineage preserved EXACTLY — no migration, no relabeling.
    return {
      profileId: 'legacy_block_first_v1', profileVersion: null,
      profileLabel: 'generated by legacy_block_first_v1',
      proposition: null, ribPropositionId: null, setupIrId: null, setupSlug: null,
      stages: null, temporalOrdering: null, stateTransitions: null, timeframes: null,
      dataDependencies: null, referencesAndRegions: null, confirmations: null,
      invalidation: null, expiry: null, targetThesis: null,
      rawBlockCount: blocks.length, semanticBlockCount: null,
      evidenceState: 'none recorded (pre-structural save)',
      commercialTargetState: 'not_assessed_no_eligible_evidence',
      unsupportedCapabilities: [],
      blockExplanations: blocks.map((b) => ({ blockId: b.id, name: s(d(b).name) ?? b.type, stage: '—', why: s(d(b).description) ?? 'no recorded rationale (legacy save)' })),
    };
  }

  const machine = d(machineBlock!).machine as MachineDefinition | undefined;
  const stageBlocks = blocks.filter((b) => s(d(b).setupStage));
  const tfs = [...new Set(stageBlocks.map((b) => s(d(b).setupTimeframe)).filter(Boolean) as string[])];
  const byStage = (stage: string) => stageBlocks.filter((b) => d(b).setupStage === stage);
  const maxHold = d(cfg!).maxHoldBars;
  return {
    profileId: 'structural_setup_v1', profileVersion: 1,
    profileLabel: 'structural_setup_v1 v1',
    proposition: s(d(cfg!).description),
    ribPropositionId: s(d(cfg!).setupIrRef),
    setupIrId: s(d(cfg!).setupIrRef),
    setupSlug,
    stages: [...new Set(stageBlocks.map((b) => s(d(b).setupStage)!))],
    temporalOrdering: stageBlocks.filter((b) => d(b).setupTemporal).map((b) => { const t = d(b).setupTemporal as { op: string; bars: number }; return `${d(b).setupStage} (${d(b).requires}): ${t.op} ${t.bars} bars`; }),
    stateTransitions: machine ? machine.states.map((st, i, arr) => st.advanceOn === null ? `${st.name} (terminal)` : `${st.name} → ${st.advanceTo ?? arr[i + 1]?.name} (on ${st.advanceOn})`) : null,
    timeframes: tfs.length ? tfs : null,
    dataDependencies: blocks.filter((b) => b.type === BlockType.PRICE_DATA && d(b).entityId).map((b) => s(d(b).name) ?? 'series'),
    referencesAndRegions: blocks.filter((b) => d(b).regionKind).map((b) => s(d(b).name) ?? 'region'),
    confirmations: byStage('confirmation').map((b) => `${d(b).requires} — ${s(d(b).description) ?? ''}`),
    invalidation: byStage('invalidation').map((b) => `${d(b).requires} — ${s(d(b).description) ?? ''}`),
    expiry: typeof maxHold === 'number' ? `expires after ${maxHold} bars (compiled to maxHoldBars)` : null,
    targetThesis: byStage('exit').map((b) => `${d(b).requires} — ${s(d(b).description) ?? ''}`),
    rawBlockCount: blocks.length,
    semanticBlockCount: null, // recorded at generation time; not persisted in the save format
    evidenceState: 'unvalidated_exploratory (no evidence state recorded in this save)',
    commercialTargetState: 'not_assessed_no_eligible_evidence',
    unsupportedCapabilities: [],
    blockExplanations: blocks.map((b) => ({ blockId: b.id, name: s(d(b).name) ?? b.type, stage: s(d(b).setupStage) ?? '—', why: s(d(b).description) ?? '—' })),
  };
}
